import './style.css';
import './enhancements.css';
import {
  calculatePrayerTimes,
  dateForTimeZone,
  decimalHourInTimeZone,
  formatDecimalHour,
  PRAYER_METHOD_ORDER,
  PRAYER_METHODS,
  type PrayerMethod,
  type PrayerTimesResult,
} from './prayer-times';
import {
  cardinalDirection,
  normalizeDegrees,
  qiblaBearing,
  qiblaDistanceKm,
  relativeDirection,
  turnDirection,
} from './qibla';
import {
  cachedCitySearch,
  rememberCity,
  searchCities,
  type CitySearchResult,
} from './location-search';
import { loadSettings, saveSettings, type AppSettings } from './storage';

type Page = 'home' | 'prayer' | 'science' | 'qibla' | 'tasbih' | 'settings';

type OrientationEventWithCompass = DeviceOrientationEvent & {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
};

type DeviceOrientationEventConstructorWithPermission = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

const PRAYER_ROWS: Array<[keyof PrayerTimesResult, string, string]> = [
  ['fajr', 'Fajr', 'Morgengebet'],
  ['sunrise', 'Sonnenaufgang', 'Shuruq'],
  ['dhuhr', 'Dhuhr', 'Mittagsgebet'],
  ['asr', 'Asr', 'Nachmittagsgebet'],
  ['maghrib', 'Maghrib', 'Abendgebet'],
  ['isha', 'Isha', 'Nachtgebet'],
];

const TASBIH_WORDS = ['Subhanallah', 'Alhamdulliah', 'Allahu Akber'];
const appRoot = document.querySelector<HTMLDivElement>('#app');
if (!appRoot) throw new Error('App root not found');
const app: HTMLDivElement = appRoot;

let settings: AppSettings = loadSettings();
let activePage: Page = parsePage(location.hash);
let heading: number | null = null;
let orientationActive = false;
let smoothedX: number | null = null;
let smoothedY: number | null = null;
let lastCompassRenderAt = 0;
let prayerTimes = recalculatePrayerTimes();
let lastPrayerDateKey = locationDateKey(new Date());
let methodComparison: { method: PrayerMethod; times: PrayerTimesResult } | null = null;
let citySearchResults: CitySearchResult[] = [];
let citySearchTimer: number | undefined;
let citySearchController: AbortController | null = null;

function parsePage(hash: string): Page {
  const candidate = hash.replace('#/', '').split('?')[0] as Page;
  return ['home', 'prayer', 'science', 'qibla', 'tasbih', 'settings'].includes(candidate) ? candidate : 'home';
}

function currentPrayerDate(now = new Date()): Date {
  return dateForTimeZone(now, settings.timeZone);
}

function recalculatePrayerTimes(date = currentPrayerDate()): PrayerTimesResult {
  return calculatePrayerTimes(
    date,
    settings.latitude,
    settings.longitude,
    settings.method,
    settings.hanafi,
    settings.timeZone,
  );
}

function persistAndRefresh(): void {
  saveSettings(settings);
  prayerTimes = recalculatePrayerTimes();
  lastPrayerDateKey = locationDateKey(new Date());
  render();
}

function locationDateKey(now: Date): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: settings.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  }
}

function nextPrayer(now = new Date()): { label: string; time: number; remainingMs: number } | null {
  const currentHour = decimalHourInTimeZone(now, settings.timeZone);
  const entries = PRAYER_ROWS
    .filter(([key]) => key !== 'sunrise')
    .map(([key, label]) => ({ label, time: prayerTimes[key] }))
    .filter((entry) => Number.isFinite(entry.time));

  const nextToday = entries.find((entry) => entry.time > currentHour);
  if (nextToday) {
    return {
      label: nextToday.label,
      time: nextToday.time,
      remainingMs: (nextToday.time - currentHour) * 3_600_000,
    };
  }

  const tomorrow = currentPrayerDate(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowTimes = recalculatePrayerTimes(tomorrow);
  if (!Number.isFinite(tomorrowTimes.fajr)) return null;
  return {
    label: 'Fajr',
    time: tomorrowTimes.fajr,
    remainingMs: (24 - currentHour + tomorrowTimes.fajr) * 3_600_000,
  };
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatDate(date: Date): string {
  try {
    return new Intl.DateTimeFormat('de-DE', {
      timeZone: settings.timeZone,
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: '2-digit', month: 'long' }).format(date);
  }
}

function getHijriDate(date: Date): string {
  try {
    return new Intl.DateTimeFormat('de-DE-u-ca-islamic', {
      timeZone: settings.timeZone,
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    return '';
  }
}

function render(): void {
  const now = new Date();
  const next = nextPrayer(now);
  const bearing = qiblaBearing(settings.latitude, settings.longitude);
  const distance = qiblaDistanceKm(settings.latitude, settings.longitude);
  const relative = heading === null ? null : relativeDirection(bearing, heading);
  const direction = relative === null ? null : turnDirection(relative);

  app.innerHTML = `
    <div class="shell">
      <header class="topbar">
        <div>
          <p class="eyebrow">SHADOWLESS STUDIOS</p>
          <h1>4Eternity</h1>
        </div>
        <button class="location-chip" data-action="go-settings" aria-label="Standort öffnen">
          <span class="dot"></span>
          <span>${escapeHtml(settings.locationName)}</span>
        </button>
      </header>

      <main class="content">
        ${activePage === 'home' ? renderHome(now, next, bearing) : ''}
        ${activePage === 'prayer' ? renderPrayer(now) : ''}
        ${activePage === 'science' ? renderScience() : ''}
        ${activePage === 'qibla' ? renderQibla(bearing, distance, relative, direction) : ''}
        ${activePage === 'tasbih' ? renderTasbih() : ''}
        ${activePage === 'settings' ? renderSettings() : ''}
      </main>

      <nav class="bottom-nav" aria-label="Hauptnavigation">
        ${navButton('home', '⌂', 'Start')}
        ${navButton('prayer', '◷', 'Gebete')}
        ${navButton('qibla', '⌖', 'Qibla')}
        ${navButton('tasbih', '●', 'Tasbih')}
        ${navButton('settings', '⚙', 'Einstellungen')}
      </nav>
    </div>
  `;

  bindEvents();
}

function renderHome(now: Date, next: ReturnType<typeof nextPrayer>, bearing: number): string {
  return `
    <section class="hero-card">
      <div class="moon-mark">☾</div>
      <p class="muted">${formatDate(now)}</p>
      <p class="hijri">${escapeHtml(getHijriDate(now))}</p>
      <div class="next-prayer">
        <span>Nächstes Gebet</span>
        <strong>${next?.label ?? '–'}</strong>
        <time>${next ? formatDecimalHour(next.time) : '–'}</time>
      </div>
      <div class="countdown" id="countdown">${next ? formatDuration(next.remainingMs) : '--:--:--'}</div>
      <p class="muted small">${escapeHtml(PRAYER_METHODS[settings.method].label)} · ${settings.hanafi ? 'Hanafi Asr' : 'Standard Asr'}</p>
    </section>

    <section class="quick-grid">
      <button class="quick-card" data-page="prayer"><span>◷</span><b>Gebetszeiten</b><small>Heute ansehen</small></button>
      <button class="quick-card" data-page="qibla"><span>⌖</span><b>Qibla</b><small>${bearing.toFixed(1)}° ${cardinalDirection(bearing)}</small></button>
      <button class="quick-card" data-page="tasbih"><span>●</span><b>Tasbih</b><small>${settings.tasbihCount} gezählt</small></button>
      <button class="quick-card" data-page="settings"><span>⚙</span><b>Einstellungen</b><small>Ort & Methode</small></button>
    </section>

    <section class="panel compact-prayers">
      <div class="section-title"><div><p class="eyebrow">HEUTE</p><h2>Gebetszeiten</h2></div><button class="link-button" data-page="prayer">Alle</button></div>
      ${PRAYER_ROWS.map(([key, label]) => `<div class="mini-row"><span>${label}</span><strong>${formatDecimalHour(prayerTimes[key])}</strong></div>`).join('')}
    </section>
  `;
}

function renderPrayerTabs(active: 'times' | 'science'): string {
  return `
    <div class="prayer-tabs" role="tablist" aria-label="Gebetszeiten Bereiche">
      <button class="${active === 'times' ? 'active' : ''}" data-page="prayer" role="tab">Zeiten</button>
      <button class="${active === 'science' ? 'active' : ''}" data-page="science" role="tab">Wissenschaft & Berechnung</button>
    </div>
  `;
}

function renderPrayer(now: Date): string {
  return `
    ${renderPrayerTabs('times')}
    <section class="page-heading">
      <p class="eyebrow">${escapeHtml(formatDate(now))}</p>
      <h2>Gebetszeiten</h2>
      <p>${escapeHtml(settings.locationName)} · ${escapeHtml(settings.timeZone)} · ${escapeHtml(PRAYER_METHODS[settings.method].label)}</p>
    </section>
    <section class="panel prayer-list">
      ${PRAYER_ROWS.map(([key, label, subtitle]) => `
        <div class="prayer-row">
          <div><strong>${label}</strong><small>${subtitle}</small></div>
          <time>${formatDecimalHour(prayerTimes[key])}</time>
        </div>
      `).join('')}
    </section>
    <section class="info-card">
      <b>Offline berechnet</b>
      <p>Die Zeiten werden auf dem Gerät aus Datum, Koordinaten, Zeitzone und Berechnungsmethode berechnet. Nach dem ersten Laden der Web-App ist dafür keine Internetverbindung notwendig.</p>
    </section>
  `;
}

function renderScience(): string {
  const current = PRAYER_METHODS[settings.method];
  return `
    ${renderPrayerTabs('science')}
    <section class="page-heading">
      <p class="eyebrow">TRANSPARENZ</p>
      <h2>Wie 4Eternity Gebetszeiten berechnet</h2>
      <p>Keine Serverzeit und keine täglich heruntergeladene Tabelle: Die Berechnung geschieht lokal aus astronomischen Größen und der gewählten Konvention.</p>
    </section>

    <section class="science-grid">
      <article class="science-card">
        <span>01</span>
        <div><h3>Ort & Zeitzone</h3><p>Breiten- und Längengrad bestimmen den Sonnenstand. Die IANA-Zeitzone des gewählten Ortes berücksichtigt die lokale Uhrzeit und Sommerzeit.</p></div>
      </article>
      <article class="science-card">
        <span>02</span>
        <div><h3>Sonnenstand</h3><p>Aus Datum, Julianischem Datum, Sonnendeklination und Equation of Time bestimmen wir den lokalen Sonnenmittag.</p></div>
      </article>
      <article class="science-card">
        <span>03</span>
        <div><h3>Fajr & Isha</h3><p>Je nach Konvention werden unterschiedliche Dämmerungswinkel verwendet. Deshalb können zwei seriöse Methoden mehrere Minuten voneinander abweichen.</p></div>
      </article>
      <article class="science-card">
        <span>04</span>
        <div><h3>Asr</h3><p>Standard verwendet den Schattenfaktor 1; die hanafitische Berechnung Faktor 2. Diese Wahl ist unabhängig von Fajr/Isha.</p></div>
      </article>
      <article class="science-card">
        <span>05</span>
        <div><h3>Hohe Breitengrade</h3><p>Wenn die normale Dämmerungsgeometrie im Sommer nicht erreicht wird, begrenzen wir Fajr/Isha mit der winkelbasierten Nachtmethode statt einfach keine Zeit anzuzeigen.</p></div>
      </article>
    </section>

    <section class="panel method-detail">
      <div class="section-title">
        <div><p class="eyebrow">AKTIVE METHODE</p><h3>${escapeHtml(current.label)}</h3></div>
        <span class="source-badge">${current.source === 'reference' ? 'Referenz' : 'Legacy'}</span>
      </div>
      <div class="method-parameters">
        <span><small>Fajr</small><b>${current.fajrAngle}°</b></span>
        <span><small>Isha</small><b>${current.ishaMinutes ? `${current.ishaMinutes} min` : `${current.ishaAngle ?? '–'}°`}</b></span>
        <span><small>Maghrib</small><b>${current.maghribAngle ? `${current.maghribAngle}°` : 'Sonnenuntergang'}</b></span>
      </div>
      ${current.note ? `<p class="method-note">${escapeHtml(current.note)}</p>` : ''}
    </section>

    <section class="panel methods-table-panel">
      <div class="section-title"><div><p class="eyebrow">KONVENTIONEN</p><h3>Verfügbare Methoden</h3></div></div>
      <div class="methods-table-wrap">
        <table class="methods-table">
          <thead><tr><th>Methode</th><th>Fajr</th><th>Isha</th><th>Region</th></tr></thead>
          <tbody>
            ${PRAYER_METHOD_ORDER.map((method) => {
              const definition = PRAYER_METHODS[method];
              return `<tr class="${settings.method === method ? 'selected' : ''}"><td>${escapeHtml(definition.label)}</td><td>${definition.fajrAngle}°</td><td>${definition.ishaMinutes ? `${definition.ishaMinutes} min` : `${definition.ishaAngle ?? '–'}°`}</td><td>${escapeHtml(definition.region)}</td></tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
    </section>

    <section class="info-card science-sources">
      <b>Quellen & Einordnung</b>
      <p>Die astronomische Struktur folgt den üblichen Formeln für Sonnenmittag, Sonnenauf-/untergang und Dämmerungswinkel. Die internationalen Methodenparameter werden mit PrayTimes abgeglichen. Die drei historischen 4Eternity-Presets Diyanet, Fazilet und Türkiye Takvimi sind ausdrücklich als Legacy markiert, bis wir sie systematisch gegen veröffentlichte Institutionstabellen validiert haben.</p>
      <div class="source-links">
        <a href="https://praytimes.org/docs/calculation" target="_blank" rel="noreferrer">PrayTimes – Berechnung</a>
        <a href="https://praytimes.org/docs/methods" target="_blank" rel="noreferrer">PrayTimes – Methoden</a>
      </div>
    </section>
  `;
}

function renderQibla(
  bearing: number,
  distance: number,
  relative: number | null,
  direction: 'left' | 'right' | 'aligned' | null,
): string {
  const dialRotation = heading === null ? 0 : -heading;
  const needleRotation = heading === null ? bearing : relative ?? bearing;
  const guidance =
    direction === 'aligned'
      ? 'Qibla ausgerichtet'
      : direction === 'right'
        ? `${Math.abs(relative ?? 0).toFixed(1)}° nach rechts drehen`
        : direction === 'left'
          ? `${Math.abs(relative ?? 0).toFixed(1)}° nach links drehen`
          : 'Kompass aktivieren, um die Drehrichtung live zu sehen';

  return `
    <section class="page-heading centered">
      <p class="eyebrow">MEKKA</p>
      <h2>Qibla-Kompass</h2>
      <p>${bearing.toFixed(1)}° ${cardinalDirection(bearing)} · ${Math.round(distance).toLocaleString('de-DE')} km</p>
    </section>

    <section class="compass-wrap">
      <div class="compass" style="--dial-rotation:${dialRotation}deg; --needle-rotation:${needleRotation}deg">
        <div class="compass-dial">
          <span class="north">N</span><span class="east">E</span><span class="south">S</span><span class="west">W</span>
          <div class="ticks"></div>
        </div>
        <div class="qibla-needle"><span class="kaaba">◆</span></div>
        <div class="compass-center"></div>
      </div>
      <div class="guidance ${direction === 'aligned' ? 'aligned' : ''}">${guidance}</div>
      <div class="sensor-stats">
        <span><small>Qibla</small><b>${bearing.toFixed(1)}°</b></span>
        <span><small>Richtung</small><b>${heading === null ? '–' : `${heading.toFixed(1)}°`}</b></span>
      </div>
      <button class="primary-button" data-action="enable-compass">${orientationActive ? 'Kompass ist aktiv' : 'Kompass aktivieren'}</button>
      <p class="permission-note">Für Live-Ausrichtung benötigt der Browser HTTPS und Zugriff auf die Gerätesensoren. Ohne Sensor bleibt der berechnete Qibla-Winkel nutzbar.</p>
    </section>
  `;
}

function renderTasbih(): string {
  const wordIndex = Math.floor(settings.tasbihCount / 33) % TASBIH_WORDS.length;
  const currentWord = TASBIH_WORDS[wordIndex] ?? TASBIH_WORDS[0];
  const displayCount = settings.infiniteTasbih ? settings.tasbihCount : settings.tasbihCount % 99;
  const activeBeads = settings.infiniteTasbih ? settings.tasbihCount % 99 : displayCount;

  return `
    <section class="page-heading centered">
      <p class="eyebrow">DHIKR</p>
      <h2>Tasbih</h2>
      <p>${settings.infiniteTasbih ? 'Infinite Mode' : '33 × 3 · 99er Zyklus'}</p>
    </section>
    <section class="tasbih-card">
      <div class="tasbih-word">${settings.infiniteTasbih ? '∞' : escapeHtml(currentWord)}</div>
      <button class="counter-button" data-action="tasbih-count" aria-label="Tasbih erhöhen">
        <span>${displayCount}</span>
        <small>ANTIPPEN</small>
      </button>
      <div class="beads" aria-hidden="true">
        ${Array.from({ length: 99 }, (_, index) => `<i class="${index < activeBeads ? 'active' : ''}"></i>`).join('')}
      </div>
      <div class="tasbih-actions">
        <button class="secondary-button" data-action="tasbih-reset">Zurücksetzen</button>
        <label class="switch-row compact"><span>Infinite</span><input id="tasbih-infinite" type="checkbox" ${settings.infiniteTasbih ? 'checked' : ''}><i></i></label>
      </div>
    </section>
  `;
}

function deltaMinutes(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  let delta = Math.round((current - previous) * 60);
  if (delta > 720) delta -= 1440;
  if (delta < -720) delta += 1440;
  return delta;
}

function renderMethodPreview(): string {
  const definition = PRAYER_METHODS[settings.method];
  return `
    <div class="method-preview">
      <div class="method-preview-heading">
        <div><small>Heute mit</small><b>${escapeHtml(definition.label)}</b></div>
        ${methodComparison ? `<span>vs. ${escapeHtml(PRAYER_METHODS[methodComparison.method].label)}</span>` : '<span>Live-Vorschau</span>'}
      </div>
      <div class="method-preview-grid">
        ${PRAYER_ROWS.map(([key, label]) => {
          const delta = methodComparison ? deltaMinutes(prayerTimes[key], methodComparison.times[key]) : null;
          const deltaLabel = delta === null || delta === 0 ? '' : `<small class="time-delta ${delta > 0 ? 'later' : 'earlier'}">${delta > 0 ? '+' : ''}${delta} min</small>`;
          return `<div><small>${label}</small><b>${formatDecimalHour(prayerTimes[key])}</b>${deltaLabel}</div>`;
        }).join('')}
      </div>
    </div>
  `;
}

function renderSettings(): string {
  return `
    <section class="page-heading">
      <p class="eyebrow">4ETERNITY</p>
      <h2>Einstellungen</h2>
      <p>Berechnung, Standort und Verhalten</p>
    </section>

    <section class="panel settings-panel">
      <label class="field">
        <span>Berechnungsmethode</span>
        <select id="method-select">
          ${PRAYER_METHOD_ORDER.map((method) => `<option value="${method}" ${settings.method === method ? 'selected' : ''}>${escapeHtml(PRAYER_METHODS[method].label)}</option>`).join('')}
        </select>
      </label>
      ${renderMethodPreview()}
      <button class="secondary-button science-button" data-page="science">Wie werden diese Zeiten berechnet?</button>
      <label class="switch-row"><div><b>Hanafi Asr</b><small>Schattenfaktor 2 statt 1</small></div><input id="hanafi-toggle" type="checkbox" ${settings.hanafi ? 'checked' : ''}><i></i></label>
      <label class="switch-row"><div><b>Vibration</b><small>Tasbih-Feedback, wenn unterstützt</small></div><input id="vibration-toggle" type="checkbox" ${settings.vibration ? 'checked' : ''}><i></i></label>
    </section>

    <section class="panel settings-panel location-settings">
      <div class="section-title"><div><p class="eyebrow">STANDORT</p><h3>${escapeHtml(settings.locationName)}</h3></div><span class="source-badge">${settings.locationSource}</span></div>

      <div class="city-search-block">
        <label class="field">
          <span>Stadt oder Postleitzahl suchen</span>
          <input id="city-search-input" type="search" autocomplete="off" placeholder="z. B. Heilbronn, Stuttgart, Istanbul …">
        </label>
        <p id="city-search-status" class="search-hint">Ab 2 Zeichen suchen. Die Auswahl speichert Koordinaten und Zeitzone lokal.</p>
        <div id="city-search-results" class="city-results" hidden></div>
      </div>

      <div class="coordinate-grid">
        <label class="field"><span>Breitengrad</span><input id="latitude-input" type="number" step="0.000001" value="${settings.latitude}"></label>
        <label class="field"><span>Längengrad</span><input id="longitude-input" type="number" step="0.000001" value="${settings.longitude}"></label>
      </div>
      <label class="field"><span>Bezeichnung</span><input id="location-name-input" type="text" value="${escapeAttribute(settings.locationName)}"></label>
      <label class="field"><span>Zeitzone</span><input id="timezone-input" type="text" value="${escapeAttribute(settings.timeZone)}" placeholder="Europe/Berlin"></label>
      <div class="button-row">
        <button class="primary-button" data-action="use-location">Gerätestandort verwenden</button>
        <button class="secondary-button" data-action="save-location">Manuell speichern</button>
      </div>
      <p id="location-status" class="permission-note">Städtesuche benötigt Internet. Nach der Auswahl werden Gebetszeiten, Qibla und Einstellungen weiterhin lokal verwendet.</p>
    </section>

    <section class="info-card">
      <b>Offline-Fallback</b>
      <p>Ohne gespeicherten oder erlaubten Standort verwendet 4Eternity Heilbronn (49.1426929, 9.210879 · Europe/Berlin). Ein einmal ausgewählter Ort bleibt lokal gespeichert.</p>
    </section>
  `;
}

function navButton(page: Exclude<Page, 'science'>, icon: string, label: string): string {
  const isActive = activePage === page || (page === 'prayer' && activePage === 'science');
  return `<button class="nav-item ${isActive ? 'active' : ''}" data-page="${page}"><span>${icon}</span><small>${label}</small></button>`;
}

function bindEvents(): void {
  document.querySelectorAll<HTMLElement>('[data-page]').forEach((element) => {
    element.addEventListener('click', () => navigate(element.dataset.page as Page));
  });

  document.querySelector<HTMLElement>('[data-action="go-settings"]')?.addEventListener('click', () => navigate('settings'));
  document.querySelector<HTMLElement>('[data-action="enable-compass"]')?.addEventListener('click', enableCompass);
  document.querySelector<HTMLElement>('[data-action="tasbih-count"]')?.addEventListener('click', incrementTasbih);
  document.querySelector<HTMLElement>('[data-action="tasbih-reset"]')?.addEventListener('click', () => {
    settings.tasbihCount = 0;
    persistAndRefresh();
  });
  document.querySelector<HTMLInputElement>('#tasbih-infinite')?.addEventListener('change', (event) => {
    settings.infiniteTasbih = (event.currentTarget as HTMLInputElement).checked;
    persistAndRefresh();
  });

  document.querySelector<HTMLSelectElement>('#method-select')?.addEventListener('change', (event) => {
    methodComparison = { method: settings.method, times: { ...prayerTimes } };
    settings.method = (event.currentTarget as HTMLSelectElement).value as PrayerMethod;
    persistAndRefresh();
  });
  document.querySelector<HTMLInputElement>('#hanafi-toggle')?.addEventListener('change', (event) => {
    methodComparison = null;
    settings.hanafi = (event.currentTarget as HTMLInputElement).checked;
    persistAndRefresh();
  });
  document.querySelector<HTMLInputElement>('#vibration-toggle')?.addEventListener('change', (event) => {
    settings.vibration = (event.currentTarget as HTMLInputElement).checked;
    persistAndRefresh();
  });

  document.querySelector<HTMLElement>('[data-action="use-location"]')?.addEventListener('click', locateUser);
  document.querySelector<HTMLElement>('[data-action="save-location"]')?.addEventListener('click', saveManualLocation);

  const cityInput = document.querySelector<HTMLInputElement>('#city-search-input');
  cityInput?.addEventListener('input', () => queueCitySearch(cityInput.value));
}

function navigate(page: Page): void {
  activePage = page;
  location.hash = `#/${page}`;
  render();
}

function incrementTasbih(): void {
  if (settings.infiniteTasbih) {
    settings.tasbihCount += 1;
  } else {
    settings.tasbihCount = (settings.tasbihCount + 1) % 99;
  }
  if (settings.vibration && 'vibrate' in navigator) navigator.vibrate(18);
  saveSettings(settings);
  render();
}

function queueCitySearch(query: string): void {
  window.clearTimeout(citySearchTimer);
  citySearchController?.abort();
  const cached = cachedCitySearch(query);
  if (cached.length > 0) renderCitySearchResults(cached, true);

  const trimmed = query.trim();
  if (trimmed.length < 2) {
    citySearchResults = [];
    renderCitySearchResults([], false);
    setCitySearchStatus('Ab 2 Zeichen suchen. Die Auswahl speichert Koordinaten und Zeitzone lokal.');
    return;
  }

  citySearchTimer = window.setTimeout(() => runCitySearch(trimmed), 320);
}

async function runCitySearch(query: string): Promise<void> {
  citySearchController?.abort();
  citySearchController = new AbortController();
  setCitySearchStatus(navigator.onLine ? 'Suche Orte …' : 'Offline: zeige bereits gespeicherte Suchtreffer.');
  try {
    const result = await searchCities(query, document.documentElement.lang || 'de', citySearchController.signal);
    citySearchResults = result.results;
    renderCitySearchResults(result.results, result.offlineOnly);
    setCitySearchStatus(
      result.results.length === 0
        ? 'Keine passenden Orte gefunden.'
        : result.offlineOnly
          ? `${result.results.length} gespeicherte Treffer · Offline`
          : `${result.results.length} Treffer · Ort auswählen`,
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    const cached = cachedCitySearch(query);
    citySearchResults = cached;
    renderCitySearchResults(cached, true);
    setCitySearchStatus(cached.length > 0 ? 'Netzwerksuche nicht erreichbar · gespeicherte Treffer' : 'Städtesuche momentan nicht erreichbar.');
  }
}

function renderCitySearchResults(results: CitySearchResult[], cachedOnly: boolean): void {
  const container = document.querySelector<HTMLElement>('#city-search-results');
  if (!container) return;
  citySearchResults = results;
  if (results.length === 0) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  container.hidden = false;
  container.innerHTML = results.map((city, index) => {
    const place = [city.admin1, city.country].filter(Boolean).join(', ');
    return `
      <button class="city-result" data-city-index="${index}">
        <span><b>${escapeHtml(city.name)}</b><small>${escapeHtml(place || city.countryCode || '')}</small></span>
        <span class="city-meta"><small>${escapeHtml(city.timeZone)}</small><em>${city.latitude.toFixed(4)}, ${city.longitude.toFixed(4)}</em></span>
      </button>
    `;
  }).join('');

  if (cachedOnly) container.dataset.cached = 'true';
  else delete container.dataset.cached;

  container.querySelectorAll<HTMLButtonElement>('[data-city-index]').forEach((button) => {
    button.addEventListener('click', () => {
      const index = Number(button.dataset.cityIndex);
      const city = citySearchResults[index];
      if (city) selectCity(city);
    });
  });
}

function selectCity(city: CitySearchResult): void {
  rememberCity(city);
  methodComparison = null;
  settings = {
    ...settings,
    latitude: city.latitude,
    longitude: city.longitude,
    timeZone: city.timeZone,
    locationName: city.name,
    locationCountry: city.country,
    locationSource: 'search',
  };
  persistAndRefresh();
}

function setCitySearchStatus(message: string): void {
  const status = document.querySelector<HTMLElement>('#city-search-status');
  if (status) status.textContent = message;
}

function saveManualLocation(): void {
  const lat = Number(document.querySelector<HTMLInputElement>('#latitude-input')?.value);
  const lon = Number(document.querySelector<HTMLInputElement>('#longitude-input')?.value);
  const name = document.querySelector<HTMLInputElement>('#location-name-input')?.value.trim() || 'Manueller Standort';
  const timeZone = document.querySelector<HTMLInputElement>('#timezone-input')?.value.trim() || settings.timeZone;
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    setLocationStatus('Bitte gültige Koordinaten eingeben.');
    return;
  }
  try {
    new Intl.DateTimeFormat('de-DE', { timeZone }).format(new Date());
  } catch {
    setLocationStatus('Bitte eine gültige IANA-Zeitzone eingeben, z. B. Europe/Berlin.');
    return;
  }
  methodComparison = null;
  settings = { ...settings, latitude: lat, longitude: lon, timeZone, locationName: name, locationSource: 'manual' };
  persistAndRefresh();
}

function locateUser(): void {
  if (!navigator.geolocation) {
    setLocationStatus('Geolocation wird von diesem Browser nicht unterstützt.');
    return;
  }
  setLocationStatus('Standort wird ermittelt …');
  navigator.geolocation.getCurrentPosition(
    (position) => {
      methodComparison = null;
      settings = {
        ...settings,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || settings.timeZone,
        locationName: 'Aktueller Standort',
        locationCountry: undefined,
        locationSource: 'browser',
      };
      persistAndRefresh();
    },
    (error) => setLocationStatus(`Standort konnte nicht gelesen werden: ${error.message}`),
    { enableHighAccuracy: true, timeout: 10_000, maximumAge: 300_000 },
  );
}

function setLocationStatus(message: string): void {
  const status = document.querySelector<HTMLElement>('#location-status');
  if (status) status.textContent = message;
}

async function enableCompass(): Promise<void> {
  if (!('DeviceOrientationEvent' in window)) {
    alert('Dieser Browser stellt keine Kompass-/Orientation-Sensoren bereit.');
    return;
  }

  try {
    const constructor = DeviceOrientationEvent as DeviceOrientationEventConstructorWithPermission;
    if (typeof constructor.requestPermission === 'function') {
      const permission = await constructor.requestPermission();
      if (permission !== 'granted') {
        alert('Der Zugriff auf den Kompass wurde nicht erlaubt.');
        return;
      }
    }

    if (!orientationActive) {
      window.addEventListener('deviceorientationabsolute', onOrientation as EventListener, true);
      window.addEventListener('deviceorientation', onOrientation as EventListener, true);
      orientationActive = true;
    }
    render();
  } catch (error) {
    console.error(error);
    alert('Der Kompass konnte nicht aktiviert werden.');
  }
}

function onOrientation(rawEvent: Event): void {
  const event = rawEvent as OrientationEventWithCompass;
  let rawHeading: number | null = null;
  if (typeof event.webkitCompassHeading === 'number' && Number.isFinite(event.webkitCompassHeading)) {
    rawHeading = event.webkitCompassHeading;
  } else if (typeof event.alpha === 'number' && Number.isFinite(event.alpha)) {
    const screenAngle = typeof screen.orientation?.angle === 'number' ? screen.orientation.angle : 0;
    rawHeading = normalizeDegrees(360 - event.alpha + screenAngle);
  }
  if (rawHeading === null) return;

  const radians = (rawHeading * Math.PI) / 180;
  const alpha = 0.15;
  smoothedX = smoothedX === null ? Math.cos(radians) : smoothedX + alpha * (Math.cos(radians) - smoothedX);
  smoothedY = smoothedY === null ? Math.sin(radians) : smoothedY + alpha * (Math.sin(radians) - smoothedY);
  heading = normalizeDegrees((Math.atan2(smoothedY, smoothedX) * 180) / Math.PI);

  const now = performance.now();
  if (activePage === 'qibla' && now - lastCompassRenderAt >= 50) {
    lastCompassRenderAt = now;
    render();
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character] ?? character);
}

function escapeAttribute(value: string): string {
  return escapeHtml(value);
}

window.addEventListener('hashchange', () => {
  activePage = parsePage(location.hash);
  render();
});

setInterval(() => {
  const now = new Date();
  const next = nextPrayer(now);
  const countdown = document.querySelector<HTMLElement>('#countdown');
  if (countdown && next) countdown.textContent = formatDuration(next.remainingMs);

  const currentDateKey = locationDateKey(now);
  if (currentDateKey !== lastPrayerDateKey) {
    prayerTimes = recalculatePrayerTimes();
    lastPrayerDateKey = currentDateKey;
    render();
  }
}, 1000);

render();
